import {
  JSX,
  useRef,
  useState,
} from "react";
import { Text } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useAppDispatch } from "@/_hooks";
import useHandleDownload from "@/_hooks/Download/useHandleDownload";
import { clearAuthToken } from "@/_reducers/accounts.reducer";
import { useGetCurrentUserQuery } from "@/_store/currentUser/currentUser.slice";
import CustomButton from "@/app/components/CustomButton";
import Divider from "@/app/components/Divider";
import Tabs, { TabsItem } from "@/app/components/Tabs";
import Company from "@/app/(main)/profile/Company";
import Subscription from "@/app/(main)/profile/Subscription";
import Feedback from "@/app/(main)/profile/Feedback";
import Container from "@/app/components/Container";
import SupportIcon from "@/assets/icons/profile/support.svg";
import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  CLOSED_POSITION,
  COMPANY_TAB,
  SUBSCRIPTION_TAB,
  SUBSCRIPTION_WARNING_DAYS
} from "@/_utils/contstans";
import { useTabs } from "@/_hooks/useTabs";
import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";

const ProfilePage = (): JSX.Element => {
  const [bottomSheetPosition, setBottomSheetPosition] = useState<number>(CLOSED_POSITION);

  const router = useRouter();
  const dispatch = useAppDispatch();
  const bottomSheetRef = useRef<BottomSheet>(null);
  const insets: EdgeInsets = useSafeAreaInsets();

  const { data, isLoading } = useGetCurrentUserQuery();
  const { id } = useLocalSearchParams();
  const defaultActiveTab: string = id === "subscription" ? SUBSCRIPTION_TAB : COMPANY_TAB;
  const daysLeft: number | undefined = data?.subscription?.days_the_end;
  const isSubscriptionAlmostOver: boolean = daysLeft !== undefined && daysLeft <= SUBSCRIPTION_WARNING_DAYS;

  const tabs: TabsItem[] = [
    {
      key: COMPANY_TAB,
      label: "Данные компании",
      children: (
        <Company
          companyData={data}
          isLoading={isLoading}
        />
      )
    },
    {
      key: SUBSCRIPTION_TAB,
      label: "Подписка",
      children: (
        <Subscription
          daysLeft={daysLeft}
          isLoading={isLoading}
          subscriptionData={data}
          isSubscriptionAlmostOver={isSubscriptionAlmostOver}
        />
      ),
    },
  ];

  const handleLogout = async (): Promise<void> => {
    dispatch(clearAuthToken());
    await AsyncStorage.removeItem("token");
    router.replace("/login");
  };

  const openSupportForm = (): void => bottomSheetRef.current?.expand();
  const closeSupportForm = (): void => bottomSheetRef.current?.close();

  const handleDownload = useHandleDownload();

  const handleDownloadInvoice = (): void => {
    handleDownload({
      url: data?.subscription?.account_url,
      errorMessage: "При скачивании счета на оплату произошла ошибка",
    });
  };

  const { activeTab, handleTabChange } = useTabs({
    defaultActiveKey: defaultActiveTab,
    items: tabs
  });

  const isSubscriptionTab: boolean = activeTab === SUBSCRIPTION_TAB;

  return (
    <>
      <Container style={{ height: 100, flexGrow: 1, paddingHorizontal: 16, paddingBottom: insets.bottom + 100 }}>
        <Text className="text-header mb-5">Мой профиль</Text>
        <Tabs
          items={tabs}
          defaultActiveKey={defaultActiveTab}
          onChange={handleTabChange}
        />
        <Divider />
        {isSubscriptionTab && isSubscriptionAlmostOver && (
          <CustomButton
            className="mb-3"
            title="Скачать счет на оплату"
            type="primary"
            onPress={handleDownloadInvoice}
          />
        )}
        {/*//TODO: скрываем до момента готовности документов УПД*/}
        {/*{isSubscriptionTab && (*/}
        {/*  <CustomButton*/}
        {/*    className="mb-3"*/}
        {/*    title="Документы об оплате"*/}
        {/*    type="default"*/}
        {/*  />*/}
        {/*)}*/}
        <CustomButton
          className="mb-3"
          title="Поддержка"
          type="default"
          suffixIcon={<SupportIcon />}
          onPress={openSupportForm}
        />
        <CustomButton
          title="Выйти из аккаунта"
          type="text"
          textClassName="!text-red-500"
          onPress={handleLogout}
        />
      </Container>
      <BottomSheet
        ref={bottomSheetRef}
        index={-1}
        snapPoints={["85%"]}
        onChange={(position: number) => setBottomSheetPosition(position)}
        enablePanDownToClose
        backdropComponent={(props) => (
          <BottomSheetBackdrop
            {...props}
            disappearsOnIndex={-1}
            appearsOnIndex={0}
            pressBehavior="close"
          />
        )}
      >
        <BottomSheetScrollView>
          <Feedback
            closeSupportForm={closeSupportForm}
            bottomSheetPosition={bottomSheetPosition}
          />
        </BottomSheetScrollView>
      </BottomSheet>
    </>
  );
};

export default ProfilePage;
